// Recently viewed products with a best-sellers fallback.
// - Shows products the customer viewed (excluding the current product), most recent first.
// - If there are none, shows best sellers from the collection the customer opened,
//   or the product's collection, or the whole store.
// - The section header switches between the two headings via [data-rv-mode].
if (!customElements.get('rv-products')) {
  const RV_STORAGE_KEY = 'glozin__recently-viewed-products';
  const LAST_COLLECTION_KEY = 'selver__last-collection';
  // sections/rv-products-results.liquid, fetched by file name (Section Rendering API)
  // so it renders on any template
  const RESULTS_SECTION = 'rv-products-results';

  const readJSON = (key, fallback) => {
    try {
      const value = JSON.parse(localStorage.getItem(key));
      return value == null ? fallback : value;
    } catch (e) {
      return fallback;
    }
  };

  class RvProducts extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.section = this.closest('[data-rv-mode]');
      this.limit = parseInt(this.dataset.limit, 10) || 8;
      this.exclude = String(this.dataset.exclude || '');
      this.shell = this.querySelector('template[data-rv-shell]');

      new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      ).observe(this);
    }

    viewedIds() {
      const ids = readJSON(RV_STORAGE_KEY, []);
      return (Array.isArray(ids) ? ids : [])
        .map(String)
        .filter((id) => id && id !== this.exclude)
        .slice(0, this.limit);
    }

    collectionCandidates() {
      let last = '';
      try {
        last = localStorage.getItem(LAST_COLLECTION_KEY) || '';
      } catch (e) {}
      const list = [this.dataset.collection, last, this.dataset.productCollection, 'all'];
      return [...new Set(list.filter(Boolean))];
    }

    async fetchResults(url) {
      const response = await fetch(url);
      if (!response.ok) return null;
      const data = await response.json();
      if (!data || !data[RESULTS_SECTION]) return null;
      const html = new DOMParser().parseFromString(data[RESULTS_SECTION], 'text/html');
      const results = html.querySelector('[data-rv-results]');
      if (!results) return null;
      if (this.exclude) results.querySelectorAll(`.rv-pid-${this.exclude}`).forEach((el) => el.remove());
      const cards = this.cardsOf(results);
      cards.slice(this.limit).forEach((el) => el.remove());
      return cards.length ? results : null;
    }

    cardsOf(results) {
      return [...results.querySelectorAll(':scope > [class*="rv-pid-"]')];
    }

    async load() {
      let results = null;
      let mode = 'viewed';

      const ids = this.viewedIds();
      if (ids.length) {
        const query = ids.map((id) => `id:${id}`).join(' OR ');
        const url = `${this.dataset.searchUrl}?sections=${RESULTS_SECTION}&type=product&options[unavailable_products]=last&q=${encodeURIComponent(query)}`;
        results = await this.fetchResults(url).catch(() => null);
        if (results) this.sortByRecency(results, ids);
      }

      if (!results && this.dataset.bestsellers !== 'false') {
        mode = 'bestseller';
        for (const handle of this.collectionCandidates()) {
          const url = `${this.dataset.collectionsUrl}/${encodeURIComponent(handle)}?sections=${RESULTS_SECTION}&sort_by=best-selling`;
          results = await this.fetchResults(url).catch(() => null);
          if (results) break;
        }
      }

      if (!results) {
        // nothing to show: hide the whole section
        (this.closest('.shopify-section') || this.section || this).remove();
        return;
      }

      if (this.section) this.section.dataset.rvMode = mode;

      // Put the cards into the layout shell (slider or grid) rendered by the section
      const fragment = this.shell.content.cloneNode(true);
      const slot = fragment.querySelector('[data-rv-slot]');
      const cardClass = this.shell.dataset.cardClass;
      this.cardsOf(results).forEach((card) => {
        if (cardClass) card.classList.add(cardClass);
        slot.appendChild(document.importNode(card, true));
      });
      this.replaceChildren(fragment);

      if (typeof initLazyloadItem === 'function') initLazyloadItem();
      if (typeof BlsLazyloadImg !== 'undefined' && BlsLazyloadImg.init) BlsLazyloadImg.init();
    }

    // Search results come back in relevance order; put the most recently viewed first.
    sortByRecency(results, ids) {
      const cards = this.cardsOf(results);
      if (cards.length < 2) return;
      const rank = (el) => {
        const match = el.className.match(/rv-pid-(\d+)/);
        const i = match ? ids.indexOf(match[1]) : -1;
        return i === -1 ? Infinity : i;
      };
      const parent = cards[0].parentElement;
      cards.sort((a, b) => rank(a) - rank(b)).forEach((el) => parent.appendChild(el));
    }
  }

  customElements.define('rv-products', RvProducts);
}
